import { Link } from "@tanstack/react-router";
import { Card, DataTable, PageHeader, Spinner } from "@empite/ui-components";
import type { ApplicationSummary, Operator } from "../admin-client";
import { useApplications } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { StatusBadge, when } from "./common";

/**
 * Applications — every application in the suite, with the two numbers that
 * say whether turning one off matters: how many OAuth clients hang off it,
 * and how many entitlements grant it.
 */
export function Applications() {
  return (
    <RequireOperator>{(session) => <ApplicationList operator={session.operator} />}</RequireOperator>
  );
}

/** The permission the Admin API guards the register route with; repeated only
 *  to decide what to draw. */
const WRITE = "applications:write";

function ApplicationList({ operator }: { operator: Operator }) {
  const applications = useApplications();

  if (applications.isPending) {
    return <Spinner label="Loading applications" />;
  }
  if (applications.error) {
    return <ErrorAlert error={applications.error} title="Could not load the applications" />;
  }

  const rows = applications.data?.applications ?? [];

  return (
    <>
      <PageHeader
        title="Applications"
        description="What the suite offers, and what each application's clients are allowed to ask for."
        actions={
          // A route rather than a subform, and a static segment so a slug can
          // never be called "new" hard enough to shadow it — TanStack ranks a
          // static segment above a parameter whatever the order in the table.
          // Registering an application is the start of a job with several more
          // steps, so it gets a URL an operator can be sent to.
          can(operator, WRITE) ? (
            <Link className="esm-button esm-button--primary" to="/applications/new">
              New application
            </Link>
          ) : null
        }
      />
      <Card>
        <DataTable<ApplicationSummary>
          rows={rows}
          rowKey={(row) => row.id}
          empty="No application has been registered on this deployment yet."
          columns={[
            {
              key: "name",
              header: "Application",
              render: (row) => (
                <Link to="/applications/$applicationId" params={{ applicationId: row.id }}>
                  {row.name}
                </Link>
              ),
            },
            { key: "slug", header: "Slug", render: (row) => <code>{row.slug}</code> },
            { key: "suite", header: "Suite", render: (row) => row.suite_name },
            { key: "status", header: "Status", render: (row) => <StatusBadge status={row.status} /> },
            {
              key: "clients",
              header: "OAuth clients",
              align: "end",
              render: (row) => row.oauth_client_count,
            },
            {
              key: "entitlements",
              header: "Entitlements",
              align: "end",
              render: (row) => row.entitlement_count,
            },
            { key: "created", header: "Registered", render: (row) => when(row.created_at) },
          ]}
        />
      </Card>
    </>
  );
}
